import React, { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

const PayPalCheckout = ({ cartItems, addressValid, deliveryDate, deliverySlot, notes, onSuccess, onError }) => {
  const [cfg, setCfg] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch((e) => onError?.(e?.response?.data?.detail || "PayPal non configurato"));
  }, [onError]);

  const providerOptions = useMemo(
    () => cfg ? {
      "client-id": cfg.client_id,
      currency: cfg.currency || "EUR",
      intent: "capture",
      components: "buttons",
    } : null,
    [cfg]
  );

  if (!cfg) {
    return (
      <div className="flex items-center justify-center h-12 border border-sky-200 rounded-lg text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento PayPal…
      </div>
    );
  }

  return (
    <PayPalScriptProvider options={providerOptions}>
      <div className="relative" data-testid="paypal-buttons-wrapper">
        {creating && (
          <div className="absolute inset-0 bg-white/70 z-10 rounded-lg flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
          </div>
        )}
        <PayPalButtons
          style={{ layout: "vertical", shape: "rect", label: "pay", color: "blue" }}
          disabled={!addressValid || !deliveryDate || !deliverySlot}
          createOrder={async () => {
            setCreating(true);
            try {
              const r = await api.post("/orders", {
                items: cartItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                delivery_address: addressValid,
                delivery_slot: deliverySlot,
                delivery_date: deliveryDate,
                payment_method: "paypal",
                notes,
              });
              setOrderId(r.data.order_id);
              return r.data.paypal_order_id;
            } catch (e) {
              onError?.(e?.response?.data?.detail || "Errore nella creazione dell'ordine");
              throw e;
            } finally {
              setCreating(false);
            }
          }}
          onApprove={async (data) => {
            try {
              const r = await api.post(`/orders/${orderId}/capture`, {
                payment_method: "paypal",
                paypal_order_id: data.orderID,
              });
              onSuccess?.(r.data);
            } catch (e) {
              onError?.(e?.response?.data?.detail || "Pagamento non completato");
            }
          }}
          onError={(err) => {
            console.error("PayPal error:", err);
            onError?.("Errore PayPal. Riprova.");
          }}
          onCancel={() => onError?.("Pagamento annullato.")}
        />
        {cfg.mode === "sandbox" && (
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 text-center">
            ⚠ Modalità SANDBOX — nessun addebito reale
          </p>
        )}
      </div>
    </PayPalScriptProvider>
  );
};

export default PayPalCheckout;
